import React from 'react';
import { reportError } from '../../hooks/useTelemetry';

/**
 * RouteErrorBoundary — page-level error boundary.
 *
 * When wrapped around a react-router `<Outlet />` (e.g. inside SettingsLayout),
 * catches render errors in the routed page and shows a fallback card instead
 * of blanking the whole React tree. Users can still navigate to a different
 * page via the surrounding sidebar/nav.
 *
 * Resets automatically on route change (via `resetKey` prop, typically
 * `location.pathname`). This prevents the error state from persisting across
 * SPA navigation — once tripped, navigating away clears it.
 *
 * IDI-303 (2026-09-11): now reports every caught error via
 * reportError('RouteErrorBoundary', ...) — the "error reporting hook"
 * this docblock used to say was a follow-up. This does NOT require a new
 * third-party account (Sentry/etc.) — it reuses the existing telemetry
 * pipeline (useTelemetry.js's EVENTS.ERROR_OCCURRED, previously defined but
 * never emitted) and the terraform/alerting.tf CloudWatch alarm on that
 * event, so a caught render crash now reaches an operator inbox instead of
 * only a browser console nobody is watching.
 *
 * Pattern mirrors `ChartErrorBoundary.js` but with page-scale copy + layout.
 * Intentionally a separate class (not a rename of ChartErrorBoundary) so the
 * 4 existing chart callsites in DashboardNew.js remain intact.
 *
 * Introduced in PR-11 alongside the Quote Templates hotfix.
 * Route-reset added 2026-08-06 (identifier census finding).
 */
export default class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props, state) {
    // Reset error state when the route changes (resetKey changes).
    // This prevents the error boundary from persisting across SPA navigation.
    if (state.hasError && props.resetKey !== state.lastResetKey) {
      return { hasError: false, error: null, lastResetKey: props.resetKey };
    }
    if (props.resetKey !== state.lastResetKey) {
      return { lastResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary] page crashed:', error, info);
    reportError('RouteErrorBoundary', error, { componentStack: info?.componentStack?.slice(0, 1000) });
  }

  handleRetry() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (this.state.hasError) {
      const msg = this.props.fallbackMessage || 'This page ran into a problem.';
      return (
        <div
          role="alert"
          className="max-w-xl rounded-md border p-4"
          style={{
            borderColor: 'var(--color-border)',
            backgroundColor: 'var(--neutral-50)',
          }}
        >
          <h2 className="text-sm font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>
            Something went wrong
          </h2>
          <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            {msg} Try again, or navigate to another section — your data is safe.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="btn btn-secondary btn-sm focus-ring"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
